import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, ShieldCheck, ShieldAlert, ShieldQuestion, Lock, Ban,
  KeyRound, Network, FileWarning, Clock, Mail, Check, Undo2, Terminal,
  AlertTriangle, Fingerprint, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const RAFRAICHIR_MS = 60000;

const GRAVITES = {
  critique: { label: "Critique", puce: "bg-red-500", cadre: "border-red-200 bg-red-50",
              texte: "text-red-700" },
  haute:    { label: "Haute", puce: "bg-orange-500", cadre: "border-orange-200 bg-orange-50",
              texte: "text-orange-700" },
  moyenne:  { label: "Moyenne", puce: "bg-amber-500", cadre: "border-amber-200 bg-amber-50",
              texte: "text-amber-700" },
  info:     { label: "Info", puce: "bg-slate-400", cadre: "border-slate-200 bg-slate-50",
              texte: "text-slate-600" },
};

const dateFr = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit",
                                                hour: "2-digit", minute: "2-digit" }) : "—";

/** Une note de sécurité n'a pas de nuance : soit rien ne cloche, soit quelque chose cloche. */
const teinteNote = (n) => (n >= 90 ? "text-emerald-600" : n >= 60 ? "text-amber-600" : "text-red-600");
const anneauNote = (n) => (n >= 90 ? "stroke-emerald-500" : n >= 60 ? "stroke-amber-500" : "stroke-red-500");

const Anneau = ({ note }) => {
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="relative w-36 h-36 shrink-0">
      <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} className="stroke-slate-100" strokeWidth="12" fill="none" />
        <circle cx="60" cy="60" r={r} className={anneauNote(note)} strokeWidth="12" fill="none"
                strokeLinecap="round" strokeDasharray={c}
                strokeDashoffset={c - (c * note) / 100} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-bold ${teinteNote(note)}`}>{note}</span>
        <span className="text-xs text-slate-400">sur 100</span>
      </div>
    </div>
  );
};

const Constat = ({ constat, onAccepter }) => {
  const g = GRAVITES[constat.gravite] || GRAVITES.info;
  return (
    <div className={`rounded-xl border p-4 ${constat.accepte ? "border-slate-200 bg-white opacity-70" : g.cadre}`}>
      <div className="flex items-start gap-3">
        <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${constat.accepte ? "bg-slate-300" : g.puce}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-slate-900">{constat.titre}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full bg-white border ${g.texte}`}>
              {g.label}
            </span>
            {constat.cle.startsWith("derive:") && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                dérive
              </span>
            )}
            {constat.accepte && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                accepté{constat.motif ? ` — ${constat.motif}` : ""}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 mt-1 break-words">{constat.detail}</p>
          {constat.correction && (
            <p className="text-sm text-slate-600 mt-2 bg-white/70 border border-slate-200 rounded-lg p-2">
              <span className="font-medium">Correction : </span>{constat.correction}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-2 font-mono">{constat.cle}</p>
        </div>
        <Button size="sm" variant={constat.accepte ? "outline" : "ghost"}
                onClick={() => onAccepter(constat, !constat.accepte)}
                title={constat.accepte ? "Remettre sous surveillance" : "Accepter : ne plus alerter"}>
          {constat.accepte ? <><Undo2 className="w-3.5 h-3.5 mr-1" />Réactiver</>
                           : <><Check className="w-3.5 h-3.5 mr-1" />Accepter</>}
        </Button>
      </div>
    </div>
  );
};

const Bloc = ({ titre, icon: Icon, children, aside }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-slate-400" />
      <h3 className="font-semibold text-slate-800">{titre}</h3>
      {aside && <span className="ml-auto text-sm text-slate-500">{aside}</span>}
    </div>
    {children}
  </div>
);

const Ligne = ({ label, valeur, alerte }) => (
  <div className="flex items-baseline gap-2 text-sm py-1">
    <span className="text-slate-500">{label}</span>
    <div className="flex-1 border-b border-dotted border-slate-200" />
    <span className={`tabular-nums ${alerte ? "text-red-600 font-medium" : "text-slate-800"}`}>
      {valeur}
    </span>
  </div>
);

/** Historique de la note : une ligne brisée suffit, la tendance est ce qui compte. */
const Courbe = ({ points }) => {
  if (!points || points.length < 2) return null;
  const l = 100 / (points.length - 1);
  const chemin = points.map((p, i) => `${i === 0 ? "M" : "L"} ${i * l} ${40 - (p.note / 100) * 36}`).join(" ");
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-16">
      <path d={chemin} fill="none" className="stroke-indigo-500" strokeWidth="1.2"
            vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export default function SecuritePage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [controle, setControle] = useState(false);
  const [onglet, setOnglet] = useState("constats");
  const authRef = useRef({ headers: { Authorization: `Bearer ${token}` } });
  authRef.current = { headers: { Authorization: `Bearer ${token}` } };

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/securite`, authRef.current);
      setData(r.data);
    } catch (e) {
      toast.error("Lecture de l'état de sécurité impossible");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
    const t = setInterval(charger, RAFRAICHIR_MS);
    return () => clearInterval(t);
  }, [charger]);

  const controler = async () => {
    setControle(true);
    try {
      const r = await axios.post(`${API_URL}/api/securite/controler`, {}, authRef.current);
      toast.success(`Contrôle terminé — note ${r.data.note}/100`);
      await charger();
    } catch (e) {
      toast.error("Le contrôle a échoué");
    } finally {
      setControle(false);
    }
  };

  const poserReference = async () => {
    setControle(true);
    try {
      await axios.post(`${API_URL}/api/securite/reference`, {}, authRef.current);
      toast.success("État actuel figé comme référence : toute évolution sera signalée");
      await charger();
    } catch (e) {
      toast.error("Impossible de figer la référence");
    } finally {
      setControle(false);
    }
  };

  const accepter = async (constat, accepte) => {
    try {
      await axios.post(`${API_URL}/api/securite/accepter`,
                       { cle: constat.cle, accepte }, authRef.current);
      toast.success(accepte ? "Constat accepté" : "Constat remis sous surveillance");
      await charger();
    } catch (e) {
      toast.error("Enregistrement impossible");
    }
  };

  if (chargement) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </CRMLayout>
    );
  }

  if (data?.vide) {
    return (
      <CRMLayout>
        <div className="p-8 max-w-xl">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Sécurité</h1>
          <p className="text-slate-600 mb-4">
            Aucun contrôle n'a encore été enregistré. Le premier passage a lieu dans les
            minutes qui suivent le démarrage du serveur, ou tout de suite si vous le demandez.
          </p>
          <Button onClick={controler} disabled={controle}>
            {controle ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Contrôler maintenant
          </Button>
        </div>
      </CRMLayout>
    );
  }

  const c = data.comptes || {};
  const constats = data.constats || [];
  const actifs = constats.filter((x) => !x.accepte);
  const acceptes = constats.filter((x) => x.accepte);
  const etat = data.etat || {};
  const ssh = etat.ssh || {};
  const f2b = etat.fail2ban || {};
  const odoo = etat.odoo || {};

  return (
    <CRMLayout>
      <div className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              {c.critique ? <ShieldAlert className="w-6 h-6 text-red-600" />
                          : <ShieldCheck className="w-6 h-6 text-emerald-600" />}
              Sécurité
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Contrôle automatique toutes les {Math.round((data.intervalle || 900) / 60)} minutes ·
              dernier passage {dateFr(data.horodatage)} ({data.duree_ms} ms) ·
              alerte par courriel dès qu'un constat grave apparaît.
            </p>
          </div>
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <Button variant="outline" onClick={controler} disabled={controle}>
              {controle ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <RefreshCw className="w-4 h-4 mr-1" />}
              Contrôler maintenant
            </Button>
            <Button variant="outline" onClick={poserReference} disabled={controle}>
              <Fingerprint className="w-4 h-4 mr-1" />
              Figer la référence
            </Button>
          </div>
        </div>

        {/* Note + compteurs */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 flex items-center gap-8 flex-wrap">
          <Anneau note={data.note ?? 0} />
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-4 sm:min-w-[280px] sm:grid-cols-4">
            {["critique", "haute", "moyenne", "info"].map((g) => (
              <div key={g}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${GRAVITES[g].puce}`} />
                  <span className="text-xs text-slate-500">{GRAVITES[g].label}</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">{c[g] ?? 0}</p>
              </div>
            ))}
          </div>
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[220px]">
            <p className="text-xs text-slate-500 mb-1">Évolution de la note</p>
            <Courbe points={data.historique} />
            <p className="text-xs text-slate-400">
              {data.reference_posee
                ? `Référence figée le ${dateFr(data.reference_le)}`
                : "Aucune référence figée : la dérive n'est pas encore surveillée"}
            </p>
          </div>
        </div>

        {!data.reference_posee && (
          <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50 p-4 flex items-start gap-3">
            <Fingerprint className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
            <p className="text-sm text-indigo-900">
              <b>Figez la référence</b> une fois que l'état du serveur vous convient : ports ouverts,
              clés SSH de root, comptes, services, tâches planifiées. Ensuite, tout écart
              (un port qui s'ouvre, une clé qui apparaît) est signalé comme dérive — c'est ce qui
              permet de voir une intrusion, et pas seulement un mauvais réglage.
            </p>
          </div>
        )}

        <Tabs value={onglet} onValueChange={setOnglet}>
          <TabsList className="mb-4">
            <TabsTrigger value="constats">Constats ({actifs.length})</TabsTrigger>
            <TabsTrigger value="acces">Accès & bannissements</TabsTrigger>
            <TabsTrigger value="exposition">Exposition</TabsTrigger>
            <TabsTrigger value="alertes">Alertes envoyées ({(data.alertes || []).length})</TabsTrigger>
          </TabsList>

          <TabsContent value="constats" className="space-y-3">
            {actifs.length === 0 && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                <p className="text-emerald-900 text-sm">Rien à signaler au dernier contrôle.</p>
              </div>
            )}
            {actifs.map((x) => <Constat key={x.cle} constat={x} onAccepter={accepter} />)}
            {acceptes.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-slate-500 pt-4">
                  Acceptés ({acceptes.length}) — surveillés, mais sans alerte
                </h3>
                {acceptes.map((x) => <Constat key={x.cle} constat={x} onAccepter={accepter} />)}
              </>
            )}
          </TabsContent>

          <TabsContent value="acces" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Bloc titre="SSH" icon={KeyRound}
                  aside={`${ssh.echecs_24h ?? 0} tentatives échouées / 24 h`}>
              <Ligne label="Mot de passe accepté"
                     valeur={ssh.reglages?.PasswordAuthentication === "no" ? "non" : "OUI"}
                     alerte={ssh.reglages?.PasswordAuthentication !== "no"} />
              <Ligne label="Connexion root" valeur={ssh.reglages?.PermitRootLogin || "—"} />
              <Ligne label="Clés autorisées pour root" valeur={(ssh.cles_root || []).length} />
              <div className="mt-3 space-y-1">
                {(ssh.cles_root || []).map((k) => (
                  <p key={k.empreinte} className="text-xs text-slate-500 font-mono truncate">
                    {k.type} · {k.commentaire || "sans commentaire"} · {k.empreinte}
                  </p>
                ))}
              </div>
              <h4 className="text-sm font-medium text-slate-700 mt-4 mb-1">
                Connexions réussies (7 jours)
              </h4>
              {(ssh.connexions_reussies || []).length === 0 && (
                <p className="text-sm text-slate-500">Aucune.</p>
              )}
              <div className="space-y-1">
                {(ssh.connexions_reussies || []).slice().reverse().map((x, i) => (
                  <p key={i} className="text-xs text-slate-600 font-mono">
                    {x.quand} · {x.utilisateur}@{x.ip} ({x.methode})
                  </p>
                ))}
              </div>
            </Bloc>

            <Bloc titre="fail2ban" icon={Ban}
                  aside={f2b.actif ? `${f2b.bannies_maintenant ?? 0} IP bannies` : "arrêté"}>
              {!f2b.actif && <p className="text-sm text-red-600">Le service n'est pas actif.</p>}
              {Object.entries(f2b.prisons || {}).map(([nom, p]) => (
                <div key={nom} className="mb-3">
                  <Ligne label={nom} valeur={`${p.bannies} bannies · ${p.cumul} au total`} />
                  {(p.ips || []).length > 0 && (
                    <p className="text-xs text-slate-500 font-mono break-all">{p.ips.join(", ")}</p>
                  )}
                </div>
              ))}
            </Bloc>

            <Bloc titre="Comptes du serveur" icon={Terminal}>
              <Ligne label="Comptes avec shell"
                     valeur={(etat.comptes?.comptes_avec_shell || []).length} />
              <p className="text-xs text-slate-500 font-mono break-all mt-1">
                {(etat.comptes?.comptes_avec_shell || []).join(", ")}
              </p>
              <Ligne label="Membres de sudo" valeur={(etat.comptes?.sudo || []).join(", ") || "aucun"} />
            </Bloc>

            <Bloc titre="Mises à jour" icon={Activity}>
              <Ligne label="Paquets en attente" valeur={etat.maj?.paquets ?? "—"} />
              <Ligne label="dont sécurité" valeur={etat.maj?.securite ?? "—"}
                     alerte={(etat.maj?.securite ?? 0) > 0} />
              <Ligne label="Redémarrage requis" valeur={etat.maj?.redemarrage_requis ? "oui" : "non"}
                     alerte={!!etat.maj?.redemarrage_requis} />
            </Bloc>
          </TabsContent>

          <TabsContent value="exposition" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Bloc titre="Ports ouverts sur Internet" icon={Network}
                  aside={`${(etat.ports?.ports_publics || []).length} ports`}>
              <div className="flex flex-wrap gap-1.5">
                {(etat.ports?.ports_publics || []).map((p) => {
                  const hors = (etat.ports?.hors_nginx || []).includes(p);
                  return (
                    <span key={p}
                          className={`text-xs font-mono px-2 py-1 rounded border ${
                            hors ? "bg-orange-50 border-orange-200 text-orange-700"
                                 : "bg-slate-50 border-slate-200 text-slate-600"}`}
                          title={hors ? "Répond directement, sans passer par nginx" : "Porte nginx"}>
                      {p}
                    </span>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-3">
                En orange : le service répond lui-même, sans HTTPS ni filtrage nginx.
              </p>
            </Bloc>

            <Bloc titre="Gestionnaire de bases Odoo" icon={FileWarning}>
              {(odoo.gestionnaire || []).filter((g) => g.gestionnaire).length === 0 ? (
                <p className="text-sm text-emerald-700">Fermé partout.</p>
              ) : (
                <div className="space-y-1">
                  {(odoo.gestionnaire || []).filter((g) => g.gestionnaire).map((g) => (
                    <p key={g.adresse} className="text-xs font-mono text-red-700 break-all">{g.adresse}</p>
                  ))}
                </div>
              )}
              <div className="mt-3">
                {["odoo19", "odoo18"].filter((k) => odoo[k]).map((k) => (
                  <Ligne key={k} label={`${k} · list_db`} valeur={odoo[k].list_db}
                         alerte={!["false", "0", "no"].includes(odoo[k].list_db)} />
                ))}
              </div>
            </Bloc>

            <Bloc titre="Certificats" icon={Lock}>
              {(etat.certificats || []).map((x) => (
                <Ligne key={x.nom} label={x.nom} valeur={`${x.jours} jours`} alerte={x.jours < 21} />
              ))}
            </Bloc>

            <Bloc titre="Fichiers de secrets" icon={FileWarning}>
              {(etat.fichiers || []).map((f) => (
                <Ligne key={f.chemin} label={f.chemin} valeur={f.mode}
                       alerte={f.mode.endsWith("4") || f.mode.endsWith("6") || f.mode.endsWith("7")} />
              ))}
            </Bloc>
          </TabsContent>

          <TabsContent value="alertes" className="space-y-3">
            {(data.alertes || []).length === 0 && (
              <p className="text-sm text-slate-500">Aucune alerte envoyée pour l'instant.</p>
            )}
            {(data.alertes || []).map((a, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className={`w-4 h-4 ${a.envoye ? "text-emerald-600" : "text-red-600"}`} />
                  <span className="font-medium text-slate-800">{dateFr(a.horodatage)}</span>
                  <span className="text-slate-500">
                    {a.envoye ? "courriel envoyé" : "envoi en échec"} · note {a.note}/100
                  </span>
                </div>
                <ul className="mt-2 space-y-1">
                  {(a.constats || []).map((x, j) => (
                    <li key={j} className="text-sm text-slate-600">
                      <span className={`font-medium ${GRAVITES[x.gravite]?.texte}`}>
                        [{x.gravite}]
                      </span> {x.titre}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </CRMLayout>
  );
}
